import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/privacy.css';
import Header from './Header';
import Footer from './Footer';
import ContactForm from './ContactForm';

export default function Privacy() {
  const [isContactOpen, setIsContactOpen] = useState(false);
  const navigate = useNavigate();

  const handleNavigation = (section) => {
    if (section === 'home' || section === 'about') {
      navigate('/');
      setTimeout(() => {
        if (section === 'about') {
          const aboutEl = document.getElementById('about');
          if (aboutEl) {
            aboutEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    } else if (section === 'contact') {
      setIsContactOpen(true);
    }
  };

  return (
    <>
      <Header onContactClick={() => handleNavigation('contact')} onHomeClick={() => handleNavigation('home')} onAboutClick={() => handleNavigation('about')} />
      <div className="privacy-page">
        <div className="privacy-container">
          <h1>Terms and Conditions & Privacy Policy</h1>
          <p className="last-updated">Last updated: January 2026</p>

          <section className="privacy-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to Badminton Shop UK. By accessing and using our website, you agree to be bound by these Terms and Conditions. 
              If you do not agree with any part of these terms, please do not use our website.
            </p>
          </section>

          <section className="privacy-section">
            <h2>2. About Us</h2>
            <p>
              Badminton Shop UK is a curated online retailer specializing in badminton equipment, accessories, and professional 
              stringing services for UK players. We are passionate badminton players committed to providing honest advice and 
              quality products.
            </p>
          </section>

          <section className="privacy-section">
            <h2>3. Use of Website</h2>
            <p>You agree to use this website only for lawful purposes and in a way that does not infringe the rights of others. You must not:</p>
            <ul>
              <li>Use the website in any way that causes damage to the website or impairs its availability</li>
              <li>Use the website in any fraudulent manner or in connection with any criminal offense</li>
              <li>Copy, reproduce, or exploit any content from this website without our written permission</li>
              <li>Introduce viruses, trojans, or other malicious material to our website</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>4. Products and Services</h2>
            <p>
              We strive to ensure all product descriptions and prices are accurate. However, errors may occur. We reserve the right 
              to correct any errors and to update information at any time without prior notice.
            </p>
            <p>
              All orders are subject to acceptance and availability. We reserve the right to refuse or cancel any order at our discretion.
            </p>
          </section>

          <section className="privacy-section">
            <h2>5. Pricing and Payment</h2>
            <p>
              All prices are displayed in GBP (£) and include VAT where applicable. Prices are subject to change without notice. 
              Payment must be made at the time of ordering through our secure payment system.
            </p>
          </section>

          <section className="privacy-section">
            <h2>6. Privacy Policy</h2>
            <h3>6.1 Information We Collect</h3>
            <p>When you use our website or sign up for notifications, we may collect:</p>
            <ul>
              <li>Email address for notifications and marketing communications</li>
              <li>Name and contact information when you place an order or contact us</li>
              <li>Payment information (processed securely by our payment providers)</li>
              <li>Browsing information through cookies and analytics</li>
            </ul>

            <h3>6.2 How We Use Your Information</h3>
            <p>We use your information to:</p>
            <ul>
              <li>Process and fulfill your orders</li>
              <li>Send you launch notifications and updates (if you've signed up)</li>
              <li>Improve our website and customer service</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h3>6.3 Data Protection</h3>
            <p>
              We are committed to protecting your personal information. We implement appropriate security measures to prevent 
              unauthorized access, disclosure, or destruction of your data. We will never sell your personal information to third parties.
            </p>

            <h3>6.4 Cookies</h3>
            <p>
              Our website uses cookies to enhance your browsing experience and remember your preferences. By using our website, 
              you consent to our use of cookies in accordance with this policy.
            </p>

            <h3>6.5 Progressive Web App (PWA) and Local Storage</h3>
            <p>
              Our website can be installed as a Progressive Web App (PWA) on your device. When you choose to install the app:
            </p>
            <ul>
              <li><strong>Local Caching:</strong> A service worker stores website files on your device for offline access and faster loading</li>
              <li><strong>Install Preferences:</strong> We store your app installation dismissal preference in local storage for 7 days</li>
              <li><strong>No Additional Tracking:</strong> Installing the app does not enable any additional data collection beyond what our website already uses</li>
              <li><strong>Your Control:</strong> You can uninstall the app at any time from your device, which will remove all locally cached data</li>
            </ul>
            <p>
              All cached data remains on your device only and is not transmitted to our servers. The PWA feature is entirely 
              optional and enhances your browsing experience with offline functionality.
            </p>
          </section>

          <section className="privacy-section">
            <h2>7. Email Communications</h2>
            <p>
              If you sign up for our notification list, we will send you updates about our launch and special offers. 
              You can unsubscribe at any time by clicking the unsubscribe link in any email we send you.
            </p>
            <p>We respect your inbox and promise not to spam you.</p>
          </section>

          <section className="privacy-section">
            <h2>8. Third-Party Services</h2>
            <p>
              We use the following GDPR-compliant third-party services to provide and improve our services:
            </p>
            <ul>
              <li><strong>Formspree:</strong> For email collection and form processing. Formspree handles contact form submissions and email notifications securely.</li>
              <li><strong>Cloudflare:</strong> For website hosting, content delivery, and security. Cloudflare helps protect our site from malicious attacks and ensures fast, reliable access.</li>
            </ul>
            <p>
              These services may collect and process data in accordance with their own privacy policies. We ensure all third-party 
              services we use are GDPR-compliant and maintain high standards of data protection.
            </p>
          </section>

          <section className="privacy-section">
            <h2>9. Intellectual Property</h2>
            <p>
              All content on this website, including text, graphics, logos, images, and software, is the property of 
              Badminton Shop UK or its content suppliers and is protected by UK and international copyright laws.
            </p>
          </section>

          <section className="privacy-section">
            <h2>10. Links to Third-Party Websites</h2>
            <p>
              Our website may contain links to third-party websites. We are not responsible for the content or privacy 
              practices of these external sites. We encourage you to review their terms and privacy policies.
            </p>
          </section>

          <section className="privacy-section">
            <h2>11. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, Badminton Shop UK shall not be liable for any indirect, incidental, 
              special, or consequential damages arising from your use of our website or products.
            </p>
          </section>

          <section className="privacy-section">
            <h2>12. Your Rights</h2>
            <p>Under UK data protection laws, you have the right to:</p>
            <ul>
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to processing of your data</li>
              <li>Request transfer of your data</li>
            </ul>
            <p>To exercise any of these rights, please contact us at hello@badmintonshop.uk</p>
          </section>

          <section className="privacy-section">
            <h2>13. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately 
              upon posting to the website. Your continued use of the website constitutes acceptance of the updated terms.
            </p>
          </section>

          <section className="privacy-section">
            <h2>14. Governing Law</h2>
            <p>
              These Terms and Conditions are governed by and construed in accordance with the laws of England and Wales. 
              Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
          </section>

          <section className="privacy-section">
            <h2>15. Contact Us</h2>
            <p>
              If you have any questions about these Terms and Conditions or our Privacy Policy, please contact us at:
            </p>
            <p className="contact-info">
              <strong>Email:</strong> hello@badmintonshop.uk
            </p>
          </section>
        </div>
      </div>
      <Footer />
      <ContactForm isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </>
  );
}
